/**
 * Split a partially-revealed reply into the part that is safe to render as
 * markdown and the trailing fragment that is not.
 *
 * The reply bubble reveals a CHARACTER-truncated prefix (`useTypewriter`), so
 * handing that prefix straight to a markdown renderer would re-parse a
 * half-written table on every frame — rows appearing, collapsing and
 * reflowing. Splitting on the last newline means only completed LINES are
 * rendered, so a table's rows land one at a time and nothing above them moves.
 */

/** A GFM delimiter row: `|---|`, `|:--|--:|`, with or without outer pipes. */
const TABLE_SEPARATOR = /^\s*\|?(?:\s*:?-+:?\s*\|)+\s*:?-*:?\s*\|?\s*$/;

const isTableLine = (line: string): boolean => line.trimStart().startsWith("|");

/**
 * Hold back a trailing table that has no delimiter row yet.
 *
 * Without this, a header line alone renders as a paragraph of literal pipes for
 * one frame and then snaps into a table — the exact flicker this module exists
 * to prevent.
 */
function holdIncompleteTable(rendered: string): {
  keep: string;
  held: string;
} {
  const lines = rendered.split("\n");
  let index = lines.length - 1;
  // `rendered` always ends with "\n", so the final entry is an empty string.
  if (lines[index] === "") index--;

  const trailing: string[] = [];
  while (index >= 0 && isTableLine(lines[index])) {
    trailing.unshift(lines[index]);
    index--;
  }

  if (trailing.length === 0) return { keep: rendered, held: "" };
  // Two or more lines with a delimiter in second position is a real table.
  if (trailing.length >= 2 && TABLE_SEPARATOR.test(trailing[1])) {
    return { keep: rendered, held: "" };
  }

  const before = lines.slice(0, index + 1).join("\n");
  return {
    keep: before ? `${before}\n` : "",
    held: `${trailing.join("\n")}\n`,
  };
}

export function splitStreamedMarkdown(
  text: string,
  { complete = false }: { complete?: boolean } = {},
): {
  rendered: string;
  pending: string;
} {
  // A settled reply has nothing further to arrive, so nothing may be withheld.
  // Withholding here is not a cosmetic choice: a reply whose final line is a
  // table row and which does not end in a newline would lose that row for good,
  // because the guards below exist to hide a row only until the REST of it
  // lands. Once complete, everything renders.
  if (complete) return { rendered: text, pending: "" };

  const lastNewline = text.lastIndexOf("\n");
  if (lastNewline === -1) return { rendered: "", pending: text };

  const { keep, held } = holdIncompleteTable(text.slice(0, lastNewline + 1));
  const tail = text.slice(lastNewline + 1);

  // A partial table row is shown as nothing rather than as literal pipes under
  // the table — that is what makes rows appear whole, one at a time.
  const visibleTail = isTableLine(tail) ? "" : tail;

  return { rendered: keep, pending: held ? held + visibleTail : visibleTail };
}
